import { WORDS_PER_MINUTE } from "../config";

/** Retire les accents (é → e) sans toucher au reste. */
export const stripAccents = value =>
  String(value == null ? "" : value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/** "Recherche & universités" → "recherche-universites" */
export const slugify = value =>
  stripAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Forme normalisée pour la recherche : minuscules, sans accents. */
export const normalizeForSearch = value => stripAccents(value).toLowerCase();

/** "2026-09-04" → Date locale (évite le décalage UTC de `new Date("2026-09-04")`). */
export const parseISODate = iso => {
  if (iso instanceof Date) return iso;
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/** "2026-09-04" → "4 septembre 2026" */
export const formatDate = (iso, options = {}) =>
  parseISODate(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", ...options });

/** "2026-09-04" → "4 sept. 2026" */
export const formatDateShort = iso =>
  parseISODate(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

/** Supprime le balisage inline (**gras**, *italique*, `code`, [texte](url), [^n]) pour obtenir du texte brut. */
export const stripInlineMarkup = value =>
  String(value == null ? "" : value)
    .replace(/\[\^\d+\]/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();

/** Texte brut d'un bloc de contenu (pour la recherche et le temps de lecture). */
export const blockToText = block => {
  if (!block) return "";
  const parts = [];
  if (block.title) parts.push(block.title);
  if (block.text) parts.push(block.text);
  if (Array.isArray(block.items)) parts.push(block.items.join(" "));
  if (Array.isArray(block.facts)) parts.push(block.facts.map(f => `${f.label} ${f.value}`).join(" "));
  if (block.caption) parts.push(block.caption);
  if (block.code) parts.push(block.code);
  if (Array.isArray(block.rows)) parts.push(block.rows.flat().join(" "));
  return stripInlineMarkup(parts.join(" "));
};

export const contentToText = content => (Array.isArray(content) ? content.map(blockToText).join(" ") : "");

export const countWords = text =>
  String(text == null ? "" : text)
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

/** Temps de lecture estimé, en minutes (au moins 1). */
export const readingTimeMinutes = content => Math.max(1, Math.round(countWords(contentToText(content)) / WORDS_PER_MINUTE));

export const readingTimeLabel = minutes => `${minutes} min de lecture`;

/** Coupe proprement un texte à `max` caractères sur un espace, avec une ellipse. */
export const truncate = (value, max = 160) => {
  const text = stripInlineMarkup(value);
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > max * 0.6 ? lastSpace : max).trim()}…`;
};

/** "1 article" / "12 articles" */
export const pluralize = (count, singular, plural = `${singular}s`) => `${count} ${count > 1 ? plural : singular}`;
